import React, { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import DateBox from 'devextreme-react/date-box';
import { Accordion } from '../../../common/SharedComponents';
import { ListApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { LoanExpenseCategory } from '../constants/LoanExpenseCategory';
import { LoanStatusType } from '../../common/LoanConstants';
import { formatCurrency } from '../../../common/SharedFunctions';

export const LoanExpensesGrid = ({
    id,
    expenseArray,
    onChange,
    loanStatus,
    expenseSectionType = LoanExpenseCategory.PreClosing,
}) => {
    const listApi = new ListApi.ListApi(applicationInsights);
    const [nameToAdd, set_nameToAdd] = useState('');
    const [typeToAdd, set_typeToAdd] = useState('');
    const [isNameDisabled, set_nameIsDisabled] = useState();
    const [expenseTypeOptions, set_expenseTypeOptions] = useState([]);
    const [isExpanded, setIsExpanded] = useState(true);

    const styles = {
        row: { cursor: 'default', border: 'none' },
    };

    const typeToAddChanged = (selectionValue, selectionText) => {
        set_typeToAdd(selectionValue);
        if (selectionText === 'Other') {
            set_nameIsDisabled(false);
            set_nameToAdd('');
        } else {
            set_nameIsDisabled(true);
            var itemsOfType = expenseArray.filter((a) => a.loanExpenseTypeId.toString() === selectionValue);
            if (selectionValue !== '') {
                if (itemsOfType !== undefined && itemsOfType.length > 0) {
                    set_nameToAdd(selectionText + ' ' + (itemsOfType.length + 1));
                } else {
                    set_nameToAdd(selectionText);
                }
            } else {
                set_nameToAdd('');
            }
        }
    };

    const onNameChange = (index, input) => {
        if (!input) {
            return;
        }
        const items = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType);

        const target = items[index];
        if (target) {
            target.name = input;
            onChange && onChange(expenseArray);
        }
    };

    const sortList = (data) => {
        let sortedData = data.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
        return sortedData;
    };

    const loadExpenseTypeOptions = async () => {
        let data;
        switch (expenseSectionType) {
            case LoanExpenseCategory.PreClosing:
                data = await listApi.getLoanExpenseTypesPreClosing();
                set_expenseTypeOptions(sortList(data));
                return;
            case LoanExpenseCategory.PostClosing:
                data = await listApi.getLoanExpenseTypesPostClosing();
                set_expenseTypeOptions(sortList(data));
                return;
            default:
                data = await listApi.getLoanExpenseTypesPreClosing();
                set_expenseTypeOptions(sortList(data));
                return;
        }
    };

    useEffect(() => {
        loadExpenseTypeOptions();
        set_nameToAdd('');
        set_nameIsDisabled(true);
    }, [expenseArray]);

    useEffect(() => {
        let expanded = false;
        switch (expenseSectionType) {
            case LoanExpenseCategory.PreClosing:
                expanded = loanStatus > LoanStatusType.Approved ? false : true;
                setIsExpanded(expanded);
                break;
            case LoanExpenseCategory.PostClosing:
                expanded = loanStatus > LoanStatusType.Approved ? true : false;
                setIsExpanded(expanded);
                break;
            default:
                expanded = loanStatus > LoanStatusType.Approved ? false : true;
                setIsExpanded(expanded);
                break;
        }
    }, [loanStatus, expenseSectionType]);

    const onAmountChanged = (index, input) => {
        const items = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType);
        const target = items[index];
        if (target) {
            target.amount = input;
            onChange && onChange(expenseArray);
        }
    };

    const onBorrowerPaymentChanged = (index, input) => {
        const items = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType);
        const target = items[index];
        if (target) {
            target.borrowerPayment = input;
            onChange && onChange(expenseArray);
        }
    };

    const onInvoiceDateChanged = (index, input) => {
        if (!input) {
            return;
        }

        const items = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType);

        const target = items[index];
        if (target) {
            target.invoiceDate = input;
            onChange && onChange(expenseArray);
        }
    };

    const onVendorChanged = (index, input) => {
        const items = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType);
        const target = items[index];
        if (target) {
            target.vendor = input;
            onChange && onChange(expenseArray);
        }
    };

    const onNotesChanged = (index, input) => {
        const items = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType);
        const target = items[index];
        if (target) {
            target.notes = input;
            onChange && onChange(expenseArray);
        }
    };

    const onRemoveLine = (index) => {
        const items = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType);
        const target = items[index];
        expenseArray = expenseArray.filter((e) => e !== target);
        onChange && onChange(expenseArray);
    };

    const onAddLine = () => {
        if (!nameToAdd) {
            return;
        }

        var target = expenseArray.find(
            (a) => a.name.trim().toUpperCase() === nameToAdd.trim().toUpperCase() && a.loanExpenseTypeId.toString() === typeToAdd
        );
        if (target) {
            return;
        }

        expenseArray.push({
            id: 0,
            loanExpenseTypeId: typeToAdd,
            loanExpenseCategoryId: expenseSectionType,
            name: nameToAdd.trim(),
            invoiceDate: null,
            amount: null,
            borrowerPayment: null,
            vendor: null,
            notes: null,
            isSystem: false,
            applicationPropertyId: null,
        });
        onChange && onChange(expenseArray);
        set_nameToAdd('');
        set_typeToAdd('');
    };

    const calculateTotalCost = () => {
        let filteredItems = expenseArray.filter((f) => f.loanExpenseCategoryId === expenseSectionType && f.amount !== undefined);
        return formatCurrency(filteredItems.reduce((a, i) => (a = a + i.amount), 0));
    };

    const calculateTotalPayment = () => {
        let filteredItems = expenseArray.filter(
            (f) => f.loanExpenseCategoryId === expenseSectionType && f.borrowerPayment !== undefined
        );
        return formatCurrency(filteredItems.reduce((a, i) => (a = a + i.borrowerPayment), 0));
    };

    const renderLoanExpensesAccordionTitle = () => {
        switch (expenseSectionType) {
            case LoanExpenseCategory.PreClosing:
                return 'Pre-Closing';
            case LoanExpenseCategory.PostClosing:
                return 'Post-Closing';
            default:
                return 'Pre-Closing';
        }
    };
    return (
        <Accordion
            id={id}
            title={renderLoanExpensesAccordionTitle()}
            isExpanded={isExpanded}
        >
            <div className='card'>
                <div className='card-section'>
                    <div
                        className='row'
                        style={styles.formRow}
                    >
                        <div
                            className='columns small-12 large-12'
                            style={styles.formColumn}
                        >
                            <table>
                                <tbody>
                                    <tr>
                                        <th>Expense</th>
                                        <th>Invoice Date</th>
                                        <th>Cost</th>
                                        <th>Borrower Payment</th>
                                        <th>Vendor</th>
                                        <th>Notes</th>
                                        <th>Action</th>
                                    </tr>
                                    {expenseArray &&
                                        expenseArray
                                            .filter((a) => a.loanExpenseCategoryId === expenseSectionType)
                                            .map((a, index) => {
                                                return (
                                                    <tr
                                                        key={'cs_' + index}
                                                        style={styles.row}
                                                    >
                                                        <td
                                                            style={{
                                                                width: '250px',
                                                                padding: '3px',
                                                            }}
                                                        >
                                                            <input
                                                                type='text'
                                                                id={'cs_name_' + a.loanExpenseCategoryId + '_' + index}
                                                                maxLength={60}
                                                                required={true}
                                                                value={a.name}
                                                                onChange={(e) => onNameChange(index, e.target.value)}
                                                                disabled={a.isSystem}
                                                                tile='Name'
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                width: '170px',
                                                                padding: '3px',
                                                            }}
                                                        >
                                                            <DateBox
                                                                id={'cs_invoiceDate_' + a.loanExpenseCategoryId + '_' + index}
                                                                value={a.invoiceDate}
                                                                onValueChange={(e) => {
                                                                    onInvoiceDateChanged(index, e);
                                                                }}
                                                                required={false}
                                                                disabled={false}
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                width: '120px',
                                                                padding: '3px',
                                                            }}
                                                        >
                                                            <NumericFormat
                                                                thousandSeparator=','
                                                                prefix='$'
                                                                id={'cs_commitment_' + a.loanExpenseCategoryId + '_' + index}
                                                                name='commitment'
                                                                placeholder='$'
                                                                value={a.amount}
                                                                decimalScale={2}
                                                                allowNegative={false}
                                                                onValueChange={(values) => {
                                                                    onAmountChanged(index, values.floatValue);
                                                                }}
                                                                required={false}
                                                                isAllowed={(values, sourceInfo) => {
                                                                    const value = values?.floatValue;
                                                                    return value === undefined || value === null || value > 0;
                                                                }}
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                width: '120px',
                                                                padding: '3px',
                                                            }}
                                                        >
                                                            <NumericFormat
                                                                thousandSeparator=','
                                                                prefix='$'
                                                                id={'cs_borrowerpayment_' + a.loanExpenseCategoryId + '_' + index}
                                                                name='borrowerpayment'
                                                                placeholder='$'
                                                                value={a.borrowerPayment}
                                                                decimalScale={2}
                                                                allowNegative={false}
                                                                onValueChange={(values) => {
                                                                    onBorrowerPaymentChanged(index, values.floatValue);
                                                                }}
                                                                required={false}
                                                                isAllowed={(values, sourceInfo) => {
                                                                    const value = values?.floatValue;
                                                                    return value === undefined || value === null || value > 0;
                                                                }}
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                width: '250px',
                                                                padding: '3px',
                                                            }}
                                                        >
                                                            <input
                                                                type='text'
                                                                id={'cs_vendor_' + a.loanExpenseCategoryId + '_' + index}
                                                                required={false}
                                                                value={a.vendor}
                                                                onChange={(e) => onVendorChanged(index, e.target.value)}
                                                                tile='Vendor'
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                width: '250px',
                                                                padding: '3px',
                                                            }}
                                                        >
                                                            <input
                                                                type='text'
                                                                id={'cs_notes_' + a.loanExpenseCategoryId + '_' + index}
                                                                required={false}
                                                                value={a.notes}
                                                                onChange={(e) => onNotesChanged(index, e.target.value)}
                                                                tile='Notes'
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                width: '100px',
                                                                alignContent: 'center',
                                                            }}
                                                        >
                                                            {!a.isSystem && (
                                                                <button
                                                                    className='gridButton'
                                                                    type='button'
                                                                    onClick={(e) => onRemoveLine(index)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    {expenseArray &&
                                        expenseArray.filter((i) => i.loanExpenseCategoryId === expenseSectionType).length > 0 && (
                                            <tr>
                                                <td>Totals:</td>
                                                <td></td>
                                                <td>{calculateTotalCost()}</td>
                                                <td>{calculateTotalPayment()}</td>
                                                <td></td>
                                                <td></td>
                                                <td></td>
                                            </tr>
                                        )}
                                    <tr style={styles.row}>
                                        <td
                                            style={{
                                                width: '300px',
                                                padding: '3px',
                                            }}
                                        >
                                            <label htmlFor='underwriterId'>Expense Type</label>
                                            <select
                                                id='typeToAdd'
                                                value={typeToAdd}
                                                onChange={(event) =>
                                                    typeToAddChanged(
                                                        event.target.value,
                                                        event.target.options[event.target.selectedIndex].text
                                                    )
                                                }
                                            >
                                                <option
                                                    key=''
                                                    value=''
                                                ></option>
                                                {expenseTypeOptions &&
                                                    expenseTypeOptions.map((exp) => {
                                                        return (
                                                            <option
                                                                key={exp.key}
                                                                value={exp.key}
                                                            >
                                                                {exp.name}
                                                            </option>
                                                        );
                                                    })}
                                            </select>
                                            <input
                                                type='text'
                                                maxLength={60}
                                                value={nameToAdd}
                                                onChange={(e) => set_nameToAdd(e.target.value)}
                                                required={false}
                                                disabled={isNameDisabled}
                                                placeholder="Enter Name and click 'Add'..."
                                            />
                                        </td>
                                        <td
                                            colSpan={3}
                                            style={{ padding: '3px' }}
                                        >
                                            <button
                                                style={{ marginTop: '35px' }}
                                                className='button hollow'
                                                type='button'
                                                onClick={(e) => onAddLine()}
                                                disabled={!nameToAdd}
                                            >
                                                Add
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </Accordion>
    );
};
